import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { AuthSyncService } from '@/services/auth-sync.service';
import { CustomerService } from '@/services/customer.service';
import { STATUS_CODES } from '@/constants';

/**
 * Set by the API on a response whose request carried a session it could not honour - an account
 * that was deleted, merged away or migrated, or a token that expired - after it has cleared the
 * cookie. Mirrors `authConstants.SESSION_ENDED_HEADER` in the backend.
 */
const SESSION_ENDED_HEADER = 'x-session-ended';

/**
 * Signs the customer out here as soon as the API has done so.
 *
 * The customer is only fetched once, on page load, so without this a session that ends mid-visit
 * leaves the page showing them signed in while every request is served as a guest's. Most of the
 * routes that notice answer normally and only mark the response, so success responses are checked
 * too. A 401 means the same: the API answers it only to a request with no signed-in customer.
 *
 * Clearing the customer is what the logout button does, so the cart, the wishlist and the account
 * page react exactly as they do to that.
 */
export const sessionEndedInterceptor: HttpInterceptorFn = (req, next) => {
  const customerService = inject(CustomerService);
  const authSyncService = inject(AuthSyncService);

  const signOut = () => {
    if (customerService.isLoggedIn()) {
      customerService.setCustomer(null);
      authSyncService.notifySignedOut();
    }
  };

  return next(req).pipe(
    tap({
      next: event => {
        if (event instanceof HttpResponse && event.headers.has(SESSION_ENDED_HEADER)) {
          signOut();
        }
      },
      error: res => {
        if (
          res instanceof HttpErrorResponse
          && (res.status === STATUS_CODES.unauthorized || res.headers?.has(SESSION_ENDED_HEADER))
        ) {
          signOut();
        }
      },
    }),
  );
};
